/**
 * PÅMELDING TIL LEDIGE VAKTER OG BYTTE MELLOM KOLLEGER (QA16 297.4, migrering 1321).
 *
 * `claim`: personen ber om en ledig plass på en vakt. `swap`: personen gir vakta si til en kollega,
 * eventuelt mot en av kollegaens vakter. Kollegaen svarer først (`pending_peer`), så godkjenner en med
 * `shift:assign` (`pending`). Godkjent bemanner vakta; raden står som kvittering.
 */
import { z } from 'zod';

export const SHIFT_REQUEST_KINDS = ['claim', 'swap'] as const;
export const SHIFT_REQUEST_STATUSES = [
  'pending_peer',
  'pending',
  'approved',
  'declined',
  'withdrawn',
] as const;

export const shiftRequestKindSchema = z.enum(SHIFT_REQUEST_KINDS);
export const shiftRequestStatusSchema = z.enum(SHIFT_REQUEST_STATUSES);

/** Vakta slik en forespørsel viser den: posten, tiden i arrangementets sone, og arrangementet. */
export const shiftRequestShiftSchema = z
  .object({
    id: z.uuid(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    timezone: z.string(),
    /** «Askjell · Store sal». Null for en vakt som ikke står på et arrangement. */
    eventTitle: z.string().nullable(),
    nightId: z.uuid().nullable(),
  })
  .meta({ id: 'ShiftRequestShift' });

export const shiftRequestPersonSchema = z
  .object({ id: z.uuid(), name: z.string() })
  .meta({ id: 'ShiftRequestPerson' });

export const shiftRequestDtoSchema = z
  .object({
    id: z.uuid(),
    kind: shiftRequestKindSchema,
    status: shiftRequestStatusSchema,
    shift: shiftRequestShiftSchema,
    person: shiftRequestPersonSchema,
    peer: shiftRequestPersonSchema.nullable(),
    /** Vakta kollegaen gir tilbake i et bytte. Null: kollegaen tar vakta uten å gi en tilbake. */
    peerShift: shiftRequestShiftSchema.nullable(),
    note: z.string().nullable(),
    declineReason: z.string().nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'ShiftRequest' });

export const shiftRequestListSchema = z
  .object({ items: z.array(shiftRequestDtoSchema) })
  .meta({ id: 'ShiftRequestList' });

/** En ledig vakt jeg kan melde meg på: posten, tiden, arrangementet og hvor mange som mangler. */
export const openShiftDtoSchema = z
  .object({
    shift: shiftRequestShiftSchema,
    needed: z.number().int(),
    staffed: z.number().int(),
    /** Jeg har alt meldt meg på; svaret venter. */
    requestId: z.uuid().nullable(),
  })
  .meta({ id: 'OpenShift' });

export const openShiftListSchema = z
  .object({ items: z.array(openShiftDtoSchema) })
  .meta({ id: 'OpenShiftList' });

/** En kollega jeg kan gi vakta til, med de kommende vaktene hen kan gi tilbake. */
export const shiftSwapColleagueSchema = z
  .object({
    person: shiftRequestPersonSchema,
    shifts: z.array(shiftRequestShiftSchema),
  })
  .meta({ id: 'ShiftSwapColleague' });

export const shiftSwapColleagueListSchema = z
  .object({ items: z.array(shiftSwapColleagueSchema) })
  .meta({ id: 'ShiftSwapColleagueList' });

const note = z.string().trim().min(1).max(500);

/**
 * «Meld meg på» (`claim`) eller «Bytt» (`swap`, med kollegaen og eventuelt en av hennes vakter i bytte).
 * Ett objekt og ikke en union, så det kan være en DTO-klasse; skranken sier hva som hører til hva.
 */
export const askForShiftRequestSchema = z
  .object({
    kind: shiftRequestKindSchema,
    shiftId: z.uuid(),
    peerPersonId: z.uuid().nullish(),
    peerShiftId: z.uuid().nullish(),
    note: note.nullish(),
  })
  .refine((body) => (body.kind === 'swap') === (body.peerPersonId != null), {
    message: 'A swap names the colleague; a sign-up does not.',
    path: ['peerPersonId'],
  })
  .refine((body) => body.kind === 'swap' || body.peerShiftId == null, {
    message: 'Only a swap gives a shift back.',
    path: ['peerShiftId'],
  })
  .meta({ id: 'AskForShiftRequest' });

/** Kollegaens svar på et bytte. */
export const answerShiftRequestSchema = z
  .object({ accept: z.boolean() })
  .meta({ id: 'AnswerShiftRequest' });

/** Godkjenn eller avslå, med en grunn den som spurte får se. */
export const decideShiftRequestSchema = z
  .object({ approve: z.boolean(), reason: note.nullish() })
  .meta({ id: 'DecideShiftRequest' });

export const listPendingShiftRequestsQuerySchema = z
  .object({ nightId: z.uuid().optional() })
  .meta({ id: 'ListPendingShiftRequestsQuery' });

export type ShiftRequestKind = z.infer<typeof shiftRequestKindSchema>;
export type ShiftRequestStatus = z.infer<typeof shiftRequestStatusSchema>;
export type ShiftRequestShift = z.infer<typeof shiftRequestShiftSchema>;
export type ShiftRequestDto = z.infer<typeof shiftRequestDtoSchema>;
export type ShiftRequestList = z.infer<typeof shiftRequestListSchema>;
export type OpenShiftDto = z.infer<typeof openShiftDtoSchema>;
export type OpenShiftList = z.infer<typeof openShiftListSchema>;
export type ShiftSwapColleague = z.infer<typeof shiftSwapColleagueSchema>;
export type ShiftSwapColleagueList = z.infer<typeof shiftSwapColleagueListSchema>;
export type AskForShiftRequest = z.infer<typeof askForShiftRequestSchema>;
export type AnswerShiftRequest = z.infer<typeof answerShiftRequestSchema>;
export type DecideShiftRequest = z.infer<typeof decideShiftRequestSchema>;
export type ListPendingShiftRequestsQuery = z.infer<typeof listPendingShiftRequestsQuerySchema>;
